import React from 'react';
import { useLocation } from '@reach/router';
import Link from "../utils/link";

const CollectionSubmenu = ({ handleLinkClickHandler, activeSubMenu, toggleSubMenu, navigation = [] }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path ? 'child-active' : '';
  
  // Don't filter navigation items - show all parent items in this submenu
  const shopNavItems = navigation || [];

  // Check if the active submenu belongs to an item with children
  const isActiveItemWithChildren = () => {
    if (!activeSubMenu || !navigation.length) return false;
    
    // Find the active menu item
    const activeItem = navigation.find(item => {
      const itemKey = item.title.toLowerCase().replace(/\s+/g, '-');
      return itemKey === activeSubMenu;
    });
    
    // Return true only if the active item has children
    return activeItem && activeItem.children && activeItem.children.length > 0;
  };

  const renderChildren = (children = [], parentId) => {
    if (!children || children.length === 0) return null;
    
    return (
      <ul className={`subnav__inner--child child ${activeSubMenu === parentId ? 'active' : 'inactive'}`}>
        {children.map((child) => (
          <li key={child._key} className={isActive(child.link)}>
            {child.link === location.pathname ? (
              // Use button for current page to avoid transitions
              <button className="nav-button">
                {child.title}
              </button>
            ) : (
              // Use Link without custom click handler for other pages
              <Link to={child.link || '#'}>
                {child.title}
              </Link>
            )}
          </li>
        ))}
      </ul>
    );
  };

  // Modify the static menu section (for when no navigation data)
  if (!navigation.length) {
    return (
      <div className='submenu-container shop'>
        <ul className={`subnav__inner ${activeSubMenu === 'furniture' || activeSubMenu === 'fabric' || activeSubMenu === 'accessories' ? 'active' : ''}`}>
          <li className={`parent ${activeSubMenu === 'furniture' ? 'active' : 'inactive'}`}>
            <button onClick={() => toggleSubMenu('furniture')}>
              Furniture
            </button>
            <ul className={`subnav__inner--child child ${activeSubMenu === 'furniture' ? 'active' : 'inactive'}`}>
              <li className={isActive('/shop/seating/')}>
                {location.pathname === '/shop/seating/' ? (
                  <button className="nav-button">Seating & Ottomans</button>
                ) : (
                  <Link to={'/shop/seating/'}>Seating & Ottomans</Link>
                )}
              </li>
              <li className={isActive('/shop/cabinets-tables/')}>
                {location.pathname === '/shop/cabinets-tables/' ? (
                  <button className="nav-button">Cabinets & Tables</button>
                ) : (
                  <Link to={'/shop/cabinets-tables/'}>Cabinets & Tables</Link>
                )}
              </li>
              <li className={isActive('/shop/lighting/')}>
                {location.pathname === '/shop/lighting/' ? (
                  <button className="nav-button">Lighting</button>
                ) : (
                  <Link to={'/shop/lighting/'}>Lighting</Link>
                )}
              </li>
            </ul>
          </li>
          
          <li className={`parent ${activeSubMenu === 'rugs' ? 'active' : ''}`}>
            {location.pathname === '/shop/rugs/' ? (
              <button onClick={() => toggleSubMenu('rugs')} className="nav-button">
                Rugs
              </button>
            ) : (
              <Link to={'/shop/rugs/'}>Rugs</Link>
            )}
          </li>
          
          <li className={`parent ${activeSubMenu === 'art' ? 'active' : ''}`}>
            {location.pathname === '/shop/art/' ? (
              <button onClick={() => toggleSubMenu('art')} className="nav-button">
                Art
              </button>
            ) : (
              <Link to={'/shop/art/'}>Art</Link>
            )}
          </li>
          
          <li className={`parent ${activeSubMenu === 'fabric' ? 'active' : 'inactive'}`}>
            <button onClick={() => toggleSubMenu('fabric')}>
              Fabric
            </button>
            <ul className={`subnav__inner--child child ${activeSubMenu === 'fabric' ? 'active' : 'inactive'}`}>
              <li className={isActive('/shop/fabric/')}>
                <Link to={'/shop/fabric/'}>All</Link>
              </li>
              <li className={isActive('/shop/pattern/')}>
                <Link to={'/shop/pattern/'}>Pattern</Link></li>
              <li className={isActive('/shop/plain/')}>
                <Link to={'/shop/plain/'}>Plain</Link></li>
            </ul>
          </li>
          
          <li className={`parent ${activeSubMenu === 'accessories' ? 'active' : 'inactive'}`}>
            <button onClick={() => toggleSubMenu('accessories')}>
              Accessories
            </button>
            <ul className={`subnav__inner--child child ${activeSubMenu === 'accessories' ? 'active' : 'inactive'}`}>
              <li className={isActive('/shop/cushions/')}>
                <Link to={'/shop/cushions/'}>Cushions</Link></li>
              <li className={isActive('/shop/book/')}>
                <Link to={'/shop/book/'}>Book</Link></li>
            </ul>
          </li>
        </ul>
      </div>
    );
  }

  // For the dynamic section (with navigation data)
  return (
    <div className='submenu-container shop'>
      <ul className={`subnav__inner ${activeSubMenu && isActiveItemWithChildren() ? 'active' : ''}`}>
        {shopNavItems.map((navItem) => {
          // Create a key for this item to use with activeSubMenu
          const itemKey = navItem.title.toLowerCase().replace(/\s+/g, '-');
          
          // Check if this item has children
          const hasChildren = navItem.children && navItem.children.length > 0;
          
          // We need to hide all items except the active one when:
          // 1. An item with children is active, AND
          // 2. This is not that active item
          const activeItemHasChildren = activeSubMenu && isActiveItemWithChildren();
          const shouldHide = activeItemHasChildren && activeSubMenu !== itemKey;
          
          // Create the appropriate class for this item
          let itemClass = 'parent';
          if (activeSubMenu === itemKey) {
            itemClass += ' active';
          } else if (shouldHide) {
            itemClass += ' inactive hide'; // Add an extra 'hide' class
          }
          
          return (
            <li 
              key={navItem._key} 
              className={itemClass}
            >
              {hasChildren ? (
                <>
                  <button onClick={() => toggleSubMenu(itemKey)}>
                    {navItem.title}
                  </button>
                  {renderChildren(navItem.children, itemKey)}
                </>
              ) : (
                <>
                  {/* Check if this is the current page */}
                  {navItem.link === location.pathname ? (
                    // Use a button instead of Link for current page
                    <button 
                      onClick={() => toggleSubMenu(itemKey)}
                      className="nav-button"
                    >
                      {navItem.title}
                    </button>
                  ) : (
                    // For non-current pages, use Link but without handleLinkClickHandler
                    <Link to={navItem.link || '#'}>
                      {navItem.title}
                    </Link>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CollectionSubmenu;